/**
 * SoleadLab 2.0 — IV Column Mapper (Visual)
 *
 * Click-to-assign palette: metric chips at top, spreadsheet grid below.
 * User clicks a metric chip to arm it, then clicks a column header in the
 * grid to assign. Works for both Standard (columns = params) and Transposed
 * (rows = params) layouts.
 */

import { useState, useMemo } from 'react';
import { IV_METRIC_FIELDS, type ColumnMapping } from './ivParser';

// ─── Metric colours ───────────────────────────────────────────────────────────

const FIELD_COLOR: Record<string, string> = {
  label:       '#6366f1',
  voltage_oc:  '#3b82f6',
  current_sc:  '#22c55e',
  voltage_mpp: '#8b5cf6',
  current_mpp: '#10b981',
  power_mpp:   '#f97316',
  fill_factor: '#d97706',
  irradiance:  '#ef4444',
  module_temp: '#ec4899',
};

const FIELD_BG: Record<string, string> = {
  label:       '#eef2ff',
  voltage_oc:  '#eff6ff',
  current_sc:  '#f0fdf4',
  voltage_mpp: '#f5f3ff',
  current_mpp: '#ecfdf5',
  power_mpp:   '#fff7ed',
  fill_factor: '#fffbeb',
  irradiance:  '#fff1f2',
  module_temp: '#fdf4ff',
};

// ─── Auto-detect patterns ─────────────────────────────────────────────────────

const AUTO_PATTERNS: Record<string, RegExp> = {
  label:       /\b(label|id|name|module|string|serial|sn|panel|marker)\b/i,
  voltage_oc:  /\bv_?oc\b|open.?circuit.?vol|uoc/i,
  current_sc:  /\bi_?sc\b|short.?circuit.?cur/i,
  voltage_mpp: /\bv_?mpp\b|\bvmp\b|\bu_?mpp\b/i,
  current_mpp: /\bi_?mpp\b|\bimp\b/i,
  power_mpp:   /\bp_?mpp\b|\bpmax\b|\bpmp\b|peak.?pow|max.?pow/i,
  fill_factor: /^ff$|\bff\b|fill.?factor/i,
  irradiance:  /\birr\b|irradianc|\bpoa\b/i,
  module_temp: /\btemp\b|temper|\bt_?c\b|\btmod\b|temperature/i,
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface IVColumnMapperProps {
  rawRows: unknown[][];
  headers: string[];
  previewRows: Record<string, unknown>[];
  headerRowIndex: number;
  transposed: boolean;
  mapping: ColumnMapping;
  errors: string[];
  onHeaderRowChange: (i: number) => void;
  onTransposedChange: (v: boolean) => void;
  onMappingChange: (m: ColumnMapping) => void;
  onBlockModeSelect?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function IVColumnMapper({
  rawRows,
  headers,
  previewRows,
  headerRowIndex,
  transposed,
  mapping,
  errors,
  onHeaderRowChange,
  onTransposedChange,
  onMappingChange,
  onBlockModeSelect,
}: IVColumnMapperProps) {
  const [activeField, setActiveField] = useState<string | null>(null);
  const [showHeaderPicker, setShowHeaderPicker] = useState(false);

  // Reverse lookup: columnName → metricKey
  const mappedTo = useMemo(() => {
    const out: Record<string, string> = {};
    for (const field of IV_METRIC_FIELDS) {
      const col = (mapping as Record<string, string>)[field.key];
      if (col) out[col] = field.key;
    }
    return out;
  }, [mapping]);

  const mappedCount = Object.values(mapping).filter(Boolean).length;

  // ── Auto-detect ─────────────────────────────────────────────────────────────
  const handleAutoDetect = () => {
    const next = { ...mapping };
    const usedCols = new Set(Object.values(next).filter(Boolean) as string[]);

    for (const field of IV_METRIC_FIELDS) {
      if ((next as Record<string, string>)[field.key]) continue; // already assigned
      const pat = AUTO_PATTERNS[field.key];
      if (!pat) continue;
      const match = headers.find((h) => pat.test(h) && !usedCols.has(h));
      if (match) {
        (next as Record<string, string>)[field.key] = match;
        usedCols.add(match);
      }
    }
    onMappingChange(next);
    setActiveField(null);
  };

  // ── Column / row click ───────────────────────────────────────────────────────
  const handleColClick = (colName: string) => {
    if (!activeField) return;
    const next = { ...mapping };

    // If this column is already mapped to another field, clear that
    const prevKey = mappedTo[colName];
    if (prevKey && prevKey !== activeField) {
      (next as Record<string, string>)[prevKey] = '';
    }
    (next as Record<string, string>)[activeField] = colName;
    onMappingChange(next);
    setActiveField(null);
  };

  // ── Chip click in palette ─────────────────────────────────────────────────────
  const handleChipClick = (key: string) => {
    if (activeField === key) {
      setActiveField(null); // deactivate
    } else {
      setActiveField(key);
    }
  };

  // ── Clear a mapping ───────────────────────────────────────────────────────────
  const handleClear = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = { ...mapping };
    (next as Record<string, string>)[key] = '';
    onMappingChange(next);
    if (activeField === key) setActiveField(null);
  };

  const activeFieldDef = IV_METRIC_FIELDS.find((f) => f.key === activeField);

  return (
    <div className="iv-mapper">

      {/* ── Mode toggle ───────────────────────────────────────────────── */}
      <div className="iv-mapper-mode">
        <button
          className={`iv-btn iv-btn--sm${!transposed ? ' iv-btn--active' : ''}`}
          onClick={() => { onTransposedChange(false); setActiveField(null); }}
        >
          Standard&nbsp;<span style={{ fontWeight: 400, opacity: 0.7 }}>(rows = measurements)</span>
        </button>
        <button
          className={`iv-btn iv-btn--sm${transposed ? ' iv-btn--active' : ''}`}
          onClick={() => { onTransposedChange(true); setActiveField(null); }}
        >
          Transposed&nbsp;<span style={{ fontWeight: 400, opacity: 0.7 }}>(rows = parameters)</span>
        </button>
        {onBlockModeSelect && (
          <button
            className="iv-btn iv-btn--sm iv-mapper-block-btn"
            onClick={onBlockModeSelect}
            title="Each measurement spans multiple rows (e.g. HT Italia IV600)"
          >
            ⊞ Block Template
          </button>
        )}
      </div>

      {/* ── Header row picker (standard mode only) ────────────────────── */}
      {!transposed && rawRows.length > 1 && (
        <div className="iv-mapper-header-hint">
          <span>Header row:&nbsp;<strong>Row {headerRowIndex + 1}</strong></span>
          <button
            className="iv-btn iv-btn--sm"
            onClick={() => setShowHeaderPicker((v) => !v)}
          >
            {showHeaderPicker ? 'Done' : 'Change ▾'}
          </button>
          {showHeaderPicker && (
            <div className="iv-mapper-header-picker">
              {rawRows.slice(0, 10).map((row, i) => {
                const preview = (row as unknown[]).slice(0, 4).map((v) =>
                  v != null ? String(v).slice(0, 18) : ''
                ).filter(Boolean).join(' │ ');
                return (
                  <div
                    key={i}
                    className={`iv-mapper-header-row-item${headerRowIndex === i ? ' iv-mapper-header-row-item--selected' : ''}`}
                    onClick={() => {
                      onHeaderRowChange(i);
                      setShowHeaderPicker(false);
                      setActiveField(null);
                    }}
                  >
                    <span className="iv-mapper-header-row-num">Row {i + 1}</span>
                    <span className="iv-mapper-header-row-preview">{preview || '(empty)'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {transposed && (
        <div className="iv-note iv-note--info" style={{ fontSize: 'var(--sl-text-xs)', marginBottom: 8 }}>
          Transposed mode: column A contains parameter names (e.g. "Voc [V]"). Each subsequent column is one measurement. Assign a metric to each parameter row below.
        </div>
      )}

      {/* ── Metric palette ────────────────────────────────────────────── */}
      <div className="iv-mapper-palette">
        <div className="iv-mapper-palette-label">
          {activeField
            ? <>Click a {transposed ? 'row' : 'column'} in the table to assign <strong>{activeFieldDef?.label}</strong>:</>
            : <>Tap a metric to select it, then click {transposed ? 'a row' : 'a column'} in the table:</>
          }
        </div>
        <div className="iv-mapper-palette-chips">
          {IV_METRIC_FIELDS.map((field) => {
            const col = (mapping as Record<string, string>)[field.key];
            const isAssigned = !!col;
            const isActive = activeField === field.key;
            const color = FIELD_COLOR[field.key];
            const bg = FIELD_BG[field.key];

            return (
              <button
                key={field.key}
                className={`iv-metric-tag${isActive ? ' iv-metric-tag--active' : ''}${isAssigned ? ' iv-metric-tag--assigned' : ''}`}
                style={isAssigned || isActive ? {
                  '--tag-color': color,
                  '--tag-bg': isAssigned ? bg : 'transparent',
                } as React.CSSProperties : {}}
                onClick={() => handleChipClick(field.key)}
                title={isAssigned ? `${field.label} → ${col}` : field.label}
              >
                <span className="iv-metric-tag__label">
                  {field.label}{field.required && <sup>*</sup>}
                </span>
                {isAssigned && (
                  <>
                    <span className="iv-metric-tag__col">→ {col.length > 14 ? col.slice(0, 12) + '…' : col}</span>
                    <span className="iv-metric-tag__clear" onClick={(e) => handleClear(field.key, e)}>×</span>
                  </>
                )}
              </button>
            );
          })}
          <button className="iv-btn iv-btn--sm iv-mapper-auto-btn" onClick={handleAutoDetect} title="Try to auto-detect columns by name">
            ✦ Auto-Detect
          </button>
        </div>
      </div>

      {/* ── Errors ────────────────────────────────────────────────────── */}
      {errors.length > 0 && (
        <div className="iv-note iv-note--error" style={{ margin: '6px 0' }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      {/* ── Spreadsheet grid ──────────────────────────────────────────── */}
      {headers.length > 0 && (
        <div className="iv-mapper-grid-wrap">
          {!transposed ? (
            /* Standard mode: clickable column headers */
            <table className="iv-mapper-grid">
              <thead>
                {/* Chip row */}
                <tr>
                  {headers.map((col) => {
                    const metricKey = mappedTo[col];
                    const field = metricKey ? IV_METRIC_FIELDS.find((f) => f.key === metricKey) : null;
                    const color = metricKey ? FIELD_COLOR[metricKey] : undefined;
                    const bg = metricKey ? FIELD_BG[metricKey] : undefined;
                    const isClickable = !!activeField && !metricKey;
                    const isActiveCol = activeField && mappedTo[col] === activeField;

                    return (
                      <th
                        key={col}
                        className={`iv-mapper-chip-cell${isClickable ? ' iv-mapper-chip-cell--clickable' : ''}${metricKey ? ' iv-mapper-chip-cell--assigned' : ''}${isActiveCol ? ' iv-mapper-chip-cell--active-col' : ''}`}
                        onClick={() => handleColClick(col)}
                      >
                        {field ? (
                          <span
                            className="iv-mapper-inline-chip"
                            style={{ background: bg, color, borderColor: color }}
                          >
                            {field.label}
                          </span>
                        ) : isClickable ? (
                          <span className="iv-mapper-chip-empty">click to assign</span>
                        ) : null}
                      </th>
                    );
                  })}
                </tr>
                {/* Column name row */}
                <tr>
                  {headers.map((col) => {
                    const metricKey = mappedTo[col];
                    const isClickable = !!activeField;
                    const color = metricKey ? FIELD_COLOR[metricKey] : undefined;

                    return (
                      <th
                        key={col}
                        className={`iv-mapper-col-header${isClickable ? ' iv-mapper-col-header--clickable' : ''}${metricKey ? ' iv-mapper-col-header--assigned' : ''}`}
                        style={metricKey ? { borderBottom: `3px solid ${color}` } : {}}
                        onClick={() => handleColClick(col)}
                        title={col}
                      >
                        <span className="iv-mapper-col-header-text">{col}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 6).map((row, ri) => (
                  <tr key={ri}>
                    {headers.map((col) => {
                      const metricKey = mappedTo[col];
                      const color = metricKey ? FIELD_COLOR[metricKey] : undefined;
                      return (
                        <td
                          key={col}
                          className="iv-mapper-data-cell"
                          style={metricKey ? { borderLeft: `2px solid ${color}20` } : {}}
                        >
                          {row[col] != null ? String(row[col]).slice(0, 20) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            /* Transposed mode: clickable row labels */
            <table className="iv-mapper-grid">
              <thead>
                <tr>
                  <th className="iv-mapper-col-header iv-mapper-col-header--row-label">Parameter</th>
                  {previewRows.slice(0, 5).map((_, ci) => (
                    <th key={ci} className="iv-mapper-col-header">Measurement {ci + 1}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {headers.map((paramName) => {
                  const metricKey = mappedTo[paramName];
                  const field = metricKey ? IV_METRIC_FIELDS.find((f) => f.key === metricKey) : null;
                  const color = metricKey ? FIELD_COLOR[metricKey] : undefined;
                  const bg = metricKey ? FIELD_BG[metricKey] : undefined;
                  const isClickable = !!activeField;

                  return (
                    <tr key={paramName}>
                      <td
                        className={`iv-mapper-row-label${isClickable ? ' iv-mapper-row-label--clickable' : ''}${metricKey ? ' iv-mapper-row-label--assigned' : ''}`}
                        style={metricKey ? { borderLeft: `3px solid ${color}`, background: bg } : {}}
                        onClick={() => handleColClick(paramName)}
                        title={paramName}
                      >
                        <span className="iv-mapper-row-label-name">{paramName}</span>
                        {field && (
                          <span className="iv-mapper-inline-chip" style={{ background: bg, color, borderColor: color }}>
                            {field.label}
                          </span>
                        )}
                        {!field && isClickable && (
                          <span className="iv-mapper-chip-empty">click</span>
                        )}
                      </td>
                      {previewRows.slice(0, 5).map((row, ci) => (
                        <td key={ci} className="iv-mapper-data-cell">
                          {row[paramName] != null ? String(row[paramName]).slice(0, 16) : ''}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {headers.length === 0 && (
        <div className="iv-note" style={{ marginTop: 8 }}>
          No columns detected. Try changing the header row or layout mode.
        </div>
      )}

      {/* ── Status bar ────────────────────────────────────────────────── */}
      <div className="iv-mapper-status">
        <span className="iv-mapper-status__count">
          {mappedCount} of {IV_METRIC_FIELDS.length} metrics assigned
        </span>
        <span className="iv-mapper-status__dots">
          {IV_METRIC_FIELDS.map((f) => {
            const col = (mapping as Record<string, string>)[f.key];
            return (
              <span
                key={f.key}
                className="iv-mapper-status__dot"
                style={col ? { background: FIELD_COLOR[f.key] } : {}}
                title={`${f.label}${col ? `: ${col}` : ' (not mapped)'}`}
              />
            );
          })}
        </span>
      </div>

    </div>
  );
}
